import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, site, type FormDto, type FormFieldDto } from '../net/api';
import { Markdown } from './Markdown';
import { PolicyLinks } from './Policy';

/** Where to send someone after they sign in from a form that needs an account. */
export const NEXT_KEY = 'bb:next';

type Answer = string | string[] | boolean;

/** buddybay.net/forms/<slug>: a custom form built in the admin panel. */
export function FormPage({ slug }: { slug: string }) {
  const [form, setForm] = useState<FormDto>();
  const [blocked, setBlocked] = useState<{ code: string; message: string }>();
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string>();

  useEffect(() => {
    // Signed-in answers need the session's CSRF token, which comes with /me.
    api.me().catch(() => {}).finally(() => {
      site.form(slug).then(
        (f) => { setForm(f); document.title = `${f.title} · Buddy Bay`; },
        (e: Error) => setBlocked({ code: e instanceof ApiError ? e.code : 'error', message: e.message }),
      );
    });
  }, [slug]);

  const set = (id: string, v: Answer) => setAnswers((a) => ({ ...a, [id]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    setErrors({});
    try {
      const r = await site.submitForm(slug, answers, website);
      setDone(r.confirmation);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      if (err instanceof ApiError) {
        setErrors(err.fields);
        const first = Object.keys(err.fields)[0];
        if (first) document.getElementById(`q-${first}`)?.scrollIntoView({ block: 'center' });
      }
    } finally {
      setBusy(false);
    }
  }

  function signIn() {
    try {
      sessionStorage.setItem(NEXT_KEY, window.location.pathname);
    } catch {
      /* storage blocked: they'll just land in the game */
    }
    window.location.assign('/');
  }

  return (
    <div className="policy-page form-page">
      <header className="topbar">
        <a className="brand small" href="/">Buddy Bay</a>
        <span className="spacer" />
        <a className="btn ghost" href="/">Back to Buddy Bay</a>
      </header>
      <main className="policy-body">
        {!form && !blocked && <p className="muted">Loading…</p>}
        {blocked && (
          <article className="panel">
            <h1 className="policy-title">{blocked.code === 'not_found' ? 'Form not found' : 'This form needs an account'}</h1>
            <p>{blocked.message}</p>
            {blocked.code === 'sign_in_required' && <button className="btn" onClick={signIn}>Sign in</button>}
          </article>
        )}
        {form && (
          <article className="panel">
            {form.status === 'draft' && <p className="login-banner warning">Draft preview. Only Administrators can see this form until it's opened.</p>}
            {form.access === 'locked' && <p className="login-banner">Locked form: only Administrators can see it.</p>}
            <h1 className="policy-title">{form.title}</h1>
            {done ? (
              <div className="form-done" role="status">
                <p className="note-ok">{done}</p>
                <a className="btn" href="/">Back to Buddy Bay</a>
              </div>
            ) : (
              <>
                {form.description.trim() && <Markdown source={form.description} />}
                {form.closedReason ? (
                  <p className="login-banner warning" role="status">{form.closedReason}</p>
                ) : (
                  <form onSubmit={submit} noValidate className="custom-form">
                    {form.fields.map((f) => <Question key={f.id} field={f} value={answers[f.id]} error={errors[f.id]} onChange={(v) => set(f.id, v)} />)}
                    {/* Honeypot: hidden from people, tempting to bots. */}
                    <div className="hp" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input id="website" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.currentTarget.value)} />
                    </div>
                    {error && <p className="form-error" role="alert">{error}</p>}
                    <button className="btn wide" disabled={busy}>{busy ? 'Sending…' : 'Send'}</button>
                    {!form.signedIn && form.access === 'public' && <p className="muted small-print">You're not signed in, so your answers won't be linked to a Buddy Bay account.</p>}
                  </form>
                )}
              </>
            )}
          </article>
        )}
        <PolicyLinks />
      </main>
    </div>
  );
}

function Question({ field: f, value, error, onChange }: { field: FormFieldDto; value: Answer | undefined; error?: string; onChange: (v: Answer) => void }) {
  const id = `q-${f.id}`;
  const label = (
    <>
      {f.label}
      {f.required && <span className="req" aria-hidden="true"> *</span>}
    </>
  );
  const help = f.help ? <span className="muted small-print" id={`${id}-help`}>{f.help}</span> : null;
  const err = error ? <span className="field-error" id={`${id}-err`}>{error}</span> : null;
  const described = [f.help ? `${id}-help` : '', error ? `${id}-err` : ''].filter(Boolean).join(' ') || undefined;

  if (f.type === 'section') {
    return (
      <div className="form-section">
        <h2>{f.label}</h2>
        {f.help && <p className="muted">{f.help}</p>}
      </div>
    );
  }
  if (f.type === 'single_choice' || f.type === 'multi_choice') {
    const multi = f.type === 'multi_choice';
    const picked = multi ? ((value as string[] | undefined) ?? []) : value;
    return (
      <fieldset className="field form-choice" id={id} aria-describedby={described} aria-invalid={!!error}>
        <legend>{label}</legend>
        {help}
        {f.options.map((o) => (
          <label key={o} className="choice-row">
            <input
              type={multi ? 'checkbox' : 'radio'}
              name={id}
              checked={multi ? (picked as string[]).includes(o) : picked === o}
              onChange={(e) => onChange(multi ? (e.currentTarget.checked ? [...(picked as string[]), o] : (picked as string[]).filter((x) => x !== o)) : o)}
            />
            {o}
          </label>
        ))}
        {err}
      </fieldset>
    );
  }
  if (f.type === 'agree') {
    return (
      <div className="field" id={id}>
        <label className="choice-row">
          <input type="checkbox" checked={value === true} aria-invalid={!!error} aria-describedby={described} onChange={(e) => onChange(e.currentTarget.checked)} />
          <span>{label}</span>
        </label>
        {help}
        {err}
      </div>
    );
  }
  const common = {
    id,
    'aria-invalid': !!error,
    'aria-describedby': described,
    required: f.required,
    value: typeof value === 'string' ? value : '',
  };
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {help}
      {f.type === 'long_text' ? (
        <textarea {...common} rows={5} maxLength={f.maxLength ?? 5000} onChange={(e) => onChange(e.currentTarget.value)} />
      ) : f.type === 'dropdown' ? (
        <select {...common} onChange={(e) => onChange(e.currentTarget.value)}>
          <option value="">Choose…</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          {...common}
          type={f.type === 'short_text' ? 'text' : f.type === 'number' ? 'number' : f.type}
          maxLength={f.type === 'short_text' ? (f.maxLength ?? 200) : undefined}
          autoComplete={f.type === 'email' ? 'email' : 'off'}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
      )}
      {err}
    </div>
  );
}
